// ImageGenerator.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../utils/api';

// 그라데이션 스피너 컴포넌트
const GradientSpinner = ({ size = 20 }) => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    style={{ width: size, height: size }}
    className="rounded-full p-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400"
  >
    <div className="w-full h-full bg-white rounded-full" />
  </motion.div>
);

// Role을 gradeLevel로 매핑하는 함수
const mapRoleToGradeLevel = (role) => {
  switch (role) {
    case 'STUDENT':
      return 'elementary_low';
    case 'TEACHER':
    case 'teacher':
      return 'teacher';
    case 'ADMIN':
      return 'teacher'; // Admin uses teacher interface
    default:
      return 'elementary_low';
  }
};

const ImageGenerator = ({ role }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState('');
  const [imageHistory, setImageHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [learningSessionId, setLearningSessionId] = useState(null);

  // 매핑된 gradeLevel 사용
  const gradeLevel = mapRoleToGradeLevel(role);

  // 학년별 추천 프롬프트
  const getSuggestedPrompts = (gradeLevel) => {
    switch (gradeLevel) {
      case 'elementary_low':
        return [
          '귀여운 강아지가 공을 가지고 노는 모습',
          '무지개 색깔의 나비',
          '숲 속의 작은 토끼집',
          '바다에서 수영하는 물고기들',
          '하늘을 나는 예쁜 새',
          '꽃밭에서 뛰어노는 고양이',
          '달님과 별들이 있는 밤하늘',
          '맛있는 과일들이 가득한 나무'
        ];
      case 'elementary_high':
        return [
          '우주 정거장에서 바라본 지구',
          '고대 이집트의 피라미드와 스핑크스',
          '깊은 바다 속 신비로운 생물들',
          '미래 도시의 하늘을 나는 자동차',
          '화산 폭발의 장관',
          '북극의 오로라와 빙산',
          '열대 우림의 다양한 동식물',
          '태양계의 행성들과 위성들'
        ];
      case 'teacher':
        return [
          '교실에서 수업하는 모습',
          '학생들이 협력하여 프로젝트를 진행하는 장면',
          '과학 실험을 하는 학생들',
          '도서관에서 책을 읽는 아이들',
          '운동장에서 체육 활동하는 모습',
          '미술 시간에 그림을 그리는 학생들',
          '음악 시간에 악기를 연주하는 장면',
          '현장 학습을 나간 학생들'
        ];
      default:
        return [
          '책을 읽고 있는 귀여운 동물',
          '우주에서 떠다니는 행성들',
          '무지개 색깔의 나비',
          '숲 속의 작은 집'
        ];
    }
  };

  const suggestedPrompts = getSuggestedPrompts(gradeLevel);

  // 학년별 UI 스타일
  const getUIStyle = () => {
    switch (gradeLevel) {
      case 'elementary_low':
        return {
          containerClass: 'bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-6',
          buttonClass: 'bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 px-6 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200',
          inputClass: 'w-full p-4 border-2 border-pink-300 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 text-lg',
          promptButtonClass: 'bg-purple-200 hover:bg-purple-300 text-purple-800 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200'
        };
      case 'elementary_high':
        return {
          containerClass: 'bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl p-6',
          buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200',
          inputClass: 'w-full p-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
          promptButtonClass: 'bg-indigo-200 hover:bg-indigo-300 text-indigo-800 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200'
        };
      case 'teacher':
        return {
          containerClass: 'bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6',
          buttonClass: 'bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-md shadow-sm hover:shadow-md transition-all duration-200',
          inputClass: 'w-full p-3 border border-gray-300 rounded-md focus:border-green-500 focus:ring-2 focus:ring-green-200',
          promptButtonClass: 'bg-teal-100 hover:bg-teal-200 text-teal-800 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200'
        };
      default:
        return {
          containerClass: 'bg-white rounded-lg p-6',
          buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-md',
          inputClass: 'w-full p-3 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
          promptButtonClass: 'bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-md text-sm'
        };
    }
  };

  const uiStyle = getUIStyle();

  // 학년별 제목과 설명
  const getContent = () => {
    switch (gradeLevel) {
      case 'elementary_low':
        return {
          title: '🎨 예쁜 그림 만들기',
          description: '어떤 그림을 그려달라고 할까요? 간단하게 말해주세요!',
          placeholder: '예: 귀여운 강아지',
          generateButtonText: '그림 그려줘! ✨'
        };
      case 'elementary_high':
        return {
          title: '🎨 멋진 이미지 생성',
          description: '상상하는 것을 자세히 설명해주세요. AI가 멋진 이미지로 만들어드릴게요!',
          placeholder: '예: 우주 정거장에서 바라본 지구의 모습',
          generateButtonText: '이미지 생성하기 🚀'
        };
      case 'teacher':
        return {
          title: '🎨 고품질 이미지 생성',
          description: '교육 자료나 수업에 활용할 고품질 이미지를 생성합니다. 자세한 설명을 입력해주세요.',
          placeholder: '예: 학생들이 과학 실험을 하고 있는 교실 풍경',
          generateButtonText: '고품질 이미지 생성'
        };
      default:
        return {
          title: '🎨 이미지 생성',
          description: '원하는 이미지를 설명해주세요.',
          placeholder: '이미지 설명을 입력하세요',
          generateButtonText: '이미지 생성'
        };
    }
  };

  const content = getContent();

  // 학습 세션 시작
  const startLearningSession = async () => {
    try {
      const response = await apiClient.post('/analytics/session/start', {
        gradeLevel: gradeLevel
      });
      
      if (response.data.success) {
        setLearningSessionId(response.data.sessionId);
        console.log('📚 학습 세션 시작:', response.data.sessionId);
      }
    } catch (error) {
      console.error('학습 세션 시작 오류:', error);
    }
  };

  // 학습 세션 종료
  const endLearningSession = async () => {
    if (!learningSessionId) return;
    
    try {
      await apiClient.post('/analytics/session/end', {
        sessionId: learningSessionId
      });
      console.log('📚 학습 세션 종료:', learningSessionId);
      setLearningSessionId(null);
    } catch (error) {
      console.error('학습 세션 종료 오류:', error);
    }
  };

  // 학습 상호작용 기록
  const recordInteraction = async (prompt, result) => {
    if (!learningSessionId) {
      // 세션이 없으면 새로 시작
      await startLearningSession();
    }
    
    try {
      await apiClient.post('/analytics/interaction', {
        sessionId: learningSessionId,
        type: 'image_generation',
        content: prompt,
        response: result
      });
      console.log('📝 이미지 생성 학습 상호작용 기록됨');
    } catch (error) {
      console.error('학습 상호작용 기록 오류:', error);
    }
  };

  // 컴포넌트 마운트 시 학습 세션 시작
  useEffect(() => {
    startLearningSession();
    
    // 언마운트 시 세션 종료
    return () => {
      if (learningSessionId) {
        endLearningSession();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('이미지 설명을 입력해주세요.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedImage(null);
    setCountdown(10);

    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
    setCountdownInterval(intervalId);

    try {
      console.log('이미지 생성 요청 시도:', { prompt });
      const response = await apiClient.post('/image/generate', {
        prompt
      });
      // apiClient는 인터셉터에서 자동으로 토큰을 추가함
      
      console.log('이미지 생성 응답:', response);
      const data = response.data;

      if (data.success) {
        // 플레이스홀더 이미지인 경우와 실제 이미지인 경우를 구분
        let imageResult;
        if (typeof data.image === 'string') {
          // 플레이스홀더 이미지 (SVG base64 문자열)
          imageResult = {
            imageBase64: data.image.split(',')[1], // data:image/svg+xml;base64, 부분 제거
            originalPrompt: data.prompt,
            revisedPrompt: data.educationalPrompt,
            createdAt: new Date().toISOString(),
            isPlaceholder: data.isPlaceholder,
            imageUrl: data.image // 전체 data URL 저장
          };
          setGeneratedImage(imageResult);
        } else {
          // 실제 이미지 (객체)
          imageResult = data.image;
          setGeneratedImage(data.image);
        }
        
        // 학습 상호작용 기록
        await recordInteraction(
          data.prompt || prompt,
          data.educationalPrompt || data.revisedPrompt || '이미지 생성 성공'
        );
        
        setPrompt('');
        // 히스토리 새로고침
        loadImageHistory();
      } else {
        setError(data.error || '이미지 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('이미지 생성 오류:', error);
      
      if (error.response) {
        console.log('서버 응답 상태:', error.response.status);
        console.log('서버 응답 데이터:', error.response.data);
        
        if (error.response.status === 401) {
          setError('인증이 만료되었습니다. 다시 로그인해주세요.');
        } else {
          setError(`서버 오류: ${error.response.status} - ${error.response.data.error || '알 수 없는 오류'}`);
        }
      } else if (error.request) {
        console.log('요청은 전송되었지만 응답을 받지 못함:', error.request);
        setError('서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.');
      } else {
        console.log('오류 발생:', error.message);
        setError('서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsGenerating(false);
      clearInterval(countdownInterval);
    }
  };

  const loadImageHistory = async () => {
    try {
      console.log('이미지 히스토리 로드 시도');
      const response = await apiClient.get('/image/history?limit=6');
      // apiClient는 인터셉터에서 자동으로 토큰을 추가함

      console.log('이미지 히스토리 응답:', response);
      const data = response.data;
      if (data.success) {
        // 서버 응답 데이터를 클라이언트 형식으로 변환
        const formattedImages = data.images.map(img => ({
          id: img.id,
          originalPrompt: img.prompt,
          imageBase64: img.imageUrl ? img.imageUrl.replace(/^data:image\/[^;]+;base64,/, '') : '',
          imageUrl: img.imageUrl,
          createdAt: img.createdAt,
          metadata: img.metadata
        }));
        setImageHistory(formattedImages);
      }
    } catch (error) {
      console.error('이미지 히스토리 로드 실패:', error);
      if (error.response) {
        console.log('서버 응답:', error.response.status, error.response.data);
      }
    }
  };

  const handleSuggestedPrompt = (suggestedPrompt) => {
    setPrompt(suggestedPrompt);
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.delete(`/image/${imageId}`);
      // 성공적으로 삭제되면 히스토리에서 제거
      setImageHistory(prev => prev.filter(img => img.id !== imageId));
      console.log('이미지 삭제 완료:', imageId);
    } catch (error) {
      console.error('이미지 삭제 실패:', error);
      alert('이미지 삭제에 실패했습니다.');
    }
  };

  const handleDownloadImage = (image) => {
    try {
      const imageUrl = image.imageUrl || `data:image/png;base64,${image.imageBase64}`;
      
      // 다운로드 링크 생성
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `generated-image-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('이미지 다운로드 시작:', image.id);
    } catch (error) {
      console.error('이미지 다운로드 실패:', error);
      alert('이미지 다운로드에 실패했습니다.');
    }
  };

  // 컴포넌트 마운트 시 히스토리 로드
  React.useEffect(() => {
    loadImageHistory();
  }, []);

  // 컴포넌트 언마운트 시 카운트다운 정리
  useEffect(() => {
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [countdownInterval]);

  return (
    <div className={uiStyle.containerClass}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            {content.title}
          </h2>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            {showHistory ? '생성하기' : '내 작품들'}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {!showHistory ? (
            <motion.div
              key="generator"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* 이미지 생성 폼 */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                    {content.description}
                  </label>
                  <textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={content.placeholder}
                    className={uiStyle.inputClass}
                    rows="3"
                    maxLength="500"
                    disabled={isGenerating}
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {prompt.length}/500
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <p className="text-red-700 text-sm">{error}</p>
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isGenerating || !prompt.trim()}
                  className={uiStyle.buttonClass}
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center gap-3">
                      <GradientSpinner size={20} />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 font-medium">
                        AI가 그림을 그리는 중...
                      </span>
                      {countdown > 0 && (
                        <motion.span
                          key={countdown}
                          initial={{ scale: 1.3, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="text-purple-600 font-bold text-sm"
                        >
                          {countdown}s
                        </motion.span>
                      )}
                    </div>
                  ) : (
                    content.generateButtonText
                  )}
                </button>
              </form>

              {/* 추천 프롬프트 */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">💡 추천 아이디어</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {suggestedPrompts.map((suggestedPrompt, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedPrompt(suggestedPrompt)}
                      className={uiStyle.promptButtonClass}
                      disabled={isGenerating}
                    >
                      {suggestedPrompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* 생성된 이미지 표시 */}
              {generatedImage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">🎉 생성 완료!</h3>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <img
                      src={generatedImage.imageUrl || `data:image/png;base64,${generatedImage.imageBase64}`}
                      alt={generatedImage.originalPrompt}
                      className="w-full max-w-md mx-auto rounded-lg shadow-md"
                    />
                    <div className="mt-4 space-y-2">
                      <p className="text-sm text-gray-600">
                        <strong>내가 입력한 설명:</strong> {generatedImage.originalPrompt}
                      </p>
                      {generatedImage.revisedPrompt && (
                        <p className="text-sm text-gray-600">
                          <strong>AI가 이해한 설명:</strong> {generatedImage.revisedPrompt}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        생성 시간: {new Date(generatedImage.createdAt).toLocaleString()}
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => handleDownloadImage(generatedImage)}
                          className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          📥 이미지 다운로드
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* 이미지 히스토리 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📚 내가 만든 작품들</h3>
                {imageHistory.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {imageHistory.map((image) => (
                      <motion.div
                        key={image.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-lg shadow-md overflow-hidden"
                      >
                        <img
                          src={image.imageUrl || `data:image/png;base64,${image.imageBase64}`}
                          alt={image.originalPrompt}
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            console.error('이미지 로드 실패:', e.target.src);
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-sm" style={{display: 'none'}}>
                          이미지 로드 실패
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                            {image.originalPrompt}
                          </p>
                          <p className="text-xs text-gray-500 mb-3">
                            {new Date(image.createdAt).toLocaleDateString()}
                          </p>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleDownloadImage(image)}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-2 rounded-md transition-colors"
                              title="이미지 다운로드"
                            >
                              📥 다운로드
                            </button>
                            <button
                              onClick={() => handleDeleteImage(image.id)}
                              className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-2 rounded-md transition-colors"
                              title="이미지 삭제"
                            >
                              🗑️ 삭제
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎨</div>
                    <p className="text-gray-500">아직 생성한 이미지가 없습니다.</p>
                    <p className="text-gray-400 text-sm">첫 번째 작품을 만들어보세요!</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* 안전 가이드라인 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4"
      >
        <h4 className="font-semibold text-blue-800 mb-2">🛡️ 안전한 이미지 생성 가이드</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 교육적이고 긍정적인 내용으로 설명해주세요</li>
          <li>• 폭력적이거나 무서운 내용은 피해주세요</li>
          <li>• 모든 이미지는 선생님이 확인할 수 있습니다</li>
          <li>• 부적절한 내용은 자동으로 차단됩니다</li>
        </ul>
      </motion.div>
    </div>
  );
};

export default ImageGenerator;
